import { useTranslation } from 'next-i18next';
import HotelRoom, { MealPlan } from '../../../../types/RoomAvailability';
import ImageWithFallback from '../../common/ImageWithFallback';
import Button from '../../common/Button';
import planConfigSlice from '../../../core/planConfig/slice';
import useAppDispatch from '../../../core/redux/useAppDispatch';
import analytics from '../../../core/analytics/analytics';
import { ContentType } from '../../../core/constants';

type Props = {
  categorizedMealPlan: {
    category: string;
    mealPlan: MealPlan;
  };
  hotelRoom: HotelRoom | undefined;
  renderPrice: (mealPlan: MealPlan, textSize: string, hasTilde?: boolean) => React.ReactNode;
  getLinkUrl: (roomId: number, mealPlanId: string) => string;
  setRoomId: (roomId?: number) => void;
};

const MealPlanCard = ({
  categorizedMealPlan: { mealPlan, category },
  hotelRoom,
  renderPrice,
  getLinkUrl,
  setRoomId,
}: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const events = {
    onOpenModal: (selectedMealPlan: MealPlan) => {
      setRoomId(hotelRoom?.id);
      dispatch(
        planConfigSlice.actions.openMealPlanConfigModal({
          targetSelectedMealPlanId: selectedMealPlan?.id as unknown as number,
          newlySelectedMealPlan: selectedMealPlan,
          modalType: 'normal',
        })
      );
      analytics.google.basic.track.select(ContentType.MEAL_PLAN_DETAIL, selectedMealPlan?.id ?? '');
    },
  };

  return (
    <div className="flex flex-col items-center gap-2 p-3 md:flex-row md:p-4 lg:p-0 lg:pr-4">
      <button
        type="button"
        className="flex w-full grow justify-start gap-2"
        onClick={() => events.onOpenModal(mealPlan)}
      >
        <div className="relative size-18 flex-none">
          <ImageWithFallback
            src={mealPlan?.imageUrl || '/no-plan-selected-desktop.svg'}
            alt="meal-plan-pic-desktop"
            fallbackSrc="/no-plan-selected-desktop.svg"
            layout="fill"
            objectFit="cover"
            className="rounded-md"
          />
        </div>
        <div className="flex w-full flex-col gap-1 text-left">
          <p className="line-clamp-2 text-sm">{mealPlan?.name}</p>
          <div className="flex justify-between">
            <p className="text-small font-bold">{category}</p>
            <p className="w-fit cursor-pointer text-small text-teal-600 hover:underline">
              {t('component:plan_config.card.read_more')}
            </p>
          </div>
        </div>
      </button>
      <div className="flex w-full items-center justify-between gap-2 md:w-fit">
        <div className="flex w-fit flex-col whitespace-nowrap md:flex-row">
          <span className="text-xs text-gray-600">{t('common:label.total_price_short')}</span>
          <span className="text-xs text-gray-600">{t('common:label.include_tax')}</span>
        </div>
        <div className="flex grow items-center justify-end gap-2 whitespace-nowrap lg:gap-4">
          <div className="w-24 text-right lg:w-36">{renderPrice(mealPlan, 'medium', false)}</div>
          <Button
            color="red"
            size="small"
            className="shrink-0 px-4"
            to={getLinkUrl(hotelRoom?.id as number, mealPlan?.id as string)}
          >
            {t('common:button.select')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MealPlanCard;
